import { useApi, useBalance, useWallet } from "@/contexts";
import { bridgeTokens, transactionStatus } from "@/services/bridgeService";
import { useState, useCallback, useMemo } from "react";


interface BridgeData {
  amount: string;
  chain: string;
  recipientAddress: string;
}

export const useBridge = () => {
  const [statusModelState, setStatusModelState] = useState<transactionStatus>(null);
  const [bridgeData, setBridgeData] = useState<BridgeData>({
    amount: "",
    chain: "Base",
    recipientAddress: "",
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const { fetchBalance } = useBalance();
  const { api } = useApi();
  const { selectedAccount } = useWallet();

  // Update bridge data efficiently
  const updateBridgeData = useCallback((updates: Partial<BridgeData>) => {
    setBridgeData(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle successful finalization
  const handleFinalized = useCallback(() => {
    setBridgeData(prev => ({ ...prev, amount: "", recipientAddress: "" }));
    fetchBalance();
    setIsProcessing(false);
  }, [fetchBalance]);

  // Main bridge function with improved error handling
  const handleBridge = useCallback(async (recipientAddress: string) => {
    if (!selectedAccount || !api) {
      throw new Error("Wallet not connected or API not ready");
    }

    setIsProcessing(true);
    setStatusModelState({ status: "preparing" });
    
    try {
      await bridgeTokens(
        api,
        selectedAccount,
        {
          amount: bridgeData.amount,
          chain: bridgeData.chain,
          receipientAddress: recipientAddress,
        },
        async (status, hex, _txHash) => {
          console.log("Bridge status:", status);
          if (status === "Error") {
            console.error("Failed to bridge tokens");
            return;
          }
          if (status === "isInBlock") {
            console.log("Transaction included in block:", hex);
          }
          if (status === "isFinalized") {
            console.log("Block finalized:", hex);
          }
        },
        setStatusModelState,
        handleFinalized
      );
    } catch (error) {
      console.error(`Failed to bridge tokens: ${error}`);
      setStatusModelState({
        status: "error",
        errorMessage: error instanceof Error ? error.message : "Unknown error occurred"
      });
      setIsProcessing(false);
      throw error;
    }
  }, [api, selectedAccount, bridgeData, handleFinalized]);

  // Reset bridge state
  const resetBridge = useCallback(() => {
    setBridgeData({
      amount: "",
      chain: "Base",
      recipientAddress: "",
    });
    setStatusModelState(null);
    setIsProcessing(false);
  }, []);

  // Memoized values for performance
  const value = useMemo(() => bridgeData.amount, [bridgeData.amount]);
  const chain = useMemo(() => bridgeData.chain, [bridgeData.chain]);

  // Legacy setters for backward compatibility
  const setValue = useCallback((amount: string) => {
    updateBridgeData({ amount });
  }, [updateBridgeData]);

  const setChain = useCallback((chain: string) => {
    updateBridgeData({ chain });
  }, [updateBridgeData]);

  return {
    // Main data
    bridgeData,
    updateBridgeData,
    
    // Status
    statusModelState,
    setStatusModelState,
    isProcessing,
    
    // Actions
    handleBridge,
    resetBridge,
    
    // Legacy API for backward compatibility
    value,
    setValue,
    chain,
    setChain,
    selectedAccount,
  };
};
